import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { getPayments, getExpenses, getDailyRate, setDailyRate, checkAndResetCards } from '@/lib/api';
import { formatCurrency, formatDate, getMonthRange, getMonthName, getMonthTimestamps } from '@/lib/dateUtils';
import { calcExchangeRevenue, calcBankCostSplit, calcExchangeProfitSplit, calcNetProfit } from '@/lib/exchangeCalc';
import LogoSpinner from '@/components/LogoSpinner';
import {
  fetchStripeDashboardMetrics,
  fetchStripeDailyBalances,
  fetchStripeTransactions,
  fetchStripePayouts,
  fetchStripeBalance,
  type StripeDailyBalance,
  type StripeTransaction,
  type StripePayout,
  type StripeBalanceData,
} from '@/lib/stripeService';
import { decryptStripeKey } from '@/lib/encryption';
import { cacheGet, cacheSet, buildCacheKey, type CachedDashboardData } from '@/lib/cache';
import { supabase } from '@/lib/supabase';
import { TrendingUp, TrendingDown, DollarSign, CreditCard, Calendar, ChevronDown, X, ChartBar as BarChart3, RefreshCw, Activity, ArrowUpRight, ArrowDownRight, Wallet, ChartPie as PieChart, Banknote, Clock, ListFilter as Filter } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import AnimatedNumber from '@/components/AnimatedNumber';
import AreaChart from '@/components/charts/AreaChart';
import BarChart from '@/components/charts/BarChart';
import MiniLineChart from '@/components/charts/MiniLineChart';
import DonutChart from '@/components/charts/DonutChart';
import RevenueDetailModal from '@/pages/dashboard/RevenueDetailModal';
import DayPickerModal from '@/pages/dashboard/DayPickerModal';

type DashboardView = 'overview' | 'revenue' | 'transactions';

export default function HomePage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth, isAllTime, setIsAllTime, selectMonth } = useMonth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentsCount, setPaymentsCount] = useState(0);
  const [totalPaymentsUSD, setTotalPaymentsUSD] = useState(0);
  const [totalExpensesMVR, setTotalExpensesMVR] = useState(0);
  const [bankRate, setBankRate] = useState(15.42);
  const [creditCardRate, setCreditCardRate] = useState(15.42);
  const [blackMarketRate, setBlackMarketRate] = useState(20.25);
  const [showRateModal, setShowRateModal] = useState(false);
  const [tempBlackMarketRate, setTempBlackMarketRate] = useState('20.25');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [activeView, setActiveView] = useState<DashboardView>('overview');

  const [dailyBalances, setDailyBalances] = useState<StripeDailyBalance[]>([]);
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);
  const [payouts, setPayouts] = useState<StripePayout[]>([]);
  const [stripeBalance, setStripeBalance] = useState<StripeBalanceData | null>(null);
  const [totalStripeFees, setTotalStripeFees] = useState(0);
  const [totalGross, setTotalGross] = useState(0);
  const [debitGross, setDebitGross] = useState(0);
  const [creditGross, setCreditGross] = useState(0);
  const [debitCount, setDebitCount] = useState(0);
  const [creditCount, setCreditCount] = useState(0);
  const [totalRefunds, setTotalRefunds] = useState(0);
  const [avgTransactionSize, setAvgTransactionSize] = useState(0);
  const [stripeDataLoaded, setStripeDataLoaded] = useState(false);

  const applyCachedData = useCallback((cached: CachedDashboardData) => {
    setTotalGross(cached.totalGross);
    setTotalStripeFees(cached.totalStripeFees);
    setTotalRefunds(cached.totalRefunds);
    setTotalPaymentsUSD(cached.totalPaymentsUSD);
    setPaymentsCount(cached.paymentsCount);
    setTotalExpensesMVR(cached.totalExpensesMVR);
    setBankRate(cached.bankRate);
    setCreditCardRate(cached.creditCardRate ?? cached.bankRate);
    setBlackMarketRate(cached.blackMarketRate);
    setAvgTransactionSize(cached.avgTransactionSize);
    setDebitGross(cached.debitGross ?? cached.totalGross);
    setCreditGross(cached.creditGross ?? 0);
    setDebitCount(cached.debitCount ?? cached.paymentsCount);
    setCreditCount(cached.creditCount ?? 0);
    setDailyBalances(cached.dailyBalances);
    setTransactions(cached.transactions);
    setPayouts(cached.payouts);
    setStripeDataLoaded(cached.dailyBalances.length > 0 || cached.transactions.length > 0);
  }, []);

  useEffect(() => {
    if (!selectedBusiness) {
      navigate('/businesses', { replace: true });
      return;
    }
    let cancelled = false;

    const cacheKey = isAllTime
      ? buildCacheKey(selectedBusiness.id, 0, 0)
      : buildCacheKey(selectedBusiness.id, selectedMonth.year, selectedMonth.month);
    cacheGet<CachedDashboardData>(cacheKey).then((cached) => {
      if (cancelled) return;
      if (cached) {
        applyCachedData(cached);
        setLoading(false);
      }
    });

    loadDashboardData();

    return () => { cancelled = true; };
  }, [selectedBusiness, selectedMonth, isAllTime]);

  function getAllTimeMonths(): { year: number; month: number }[] {
    const start = { year: 2026, month: 1 };
    const now = new Date();
    const end = { year: now.getFullYear(), month: now.getMonth() + 1 };
    const months: { year: number; month: number }[] = [];
    let y = start.year;
    let m = start.month;
    while (y < end.year || (y === end.year && m <= end.month)) {
      months.push({ year: y, month: m });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return months;
  }

  async function loadAllTimeData() {
    if (!selectedBusiness) return;

    const today = formatDate(new Date());
    const stripeKey = await decryptStripeKey(selectedBusiness.stripe_key_encrypted);
    const months = getAllTimeMonths();

    const [allPayments, allExpenses, todayRate] = await Promise.all([
      getPayments(selectedBusiness.id),
      getExpenses(selectedBusiness.id),
      getDailyRate(selectedBusiness.id, today),
      checkAndResetCards(selectedBusiness.id),
    ]);

    let computedGross = 0;
    let computedFees = 0;
    let computedRefunds = 0;
    let computedNet = 0;
    let computedCount = 0;
    let computedDebitGross = 0;
    let computedDebitCount = 0;
    let computedCreditGross = 0;
    let computedCreditCount = 0;
    let allDailyBalances: StripeDailyBalance[] = [];
    let allTransactions: StripeTransaction[] = [];
    let allPayouts: StripePayout[] = [];

    if (stripeKey) {
      const monthResults = await Promise.all(
        months.map(async ({ year, month }) => {
          const ts = getMonthTimestamps(year, month);
          const [metrics, daily, txns, pouts] = await Promise.all([
            fetchStripeDashboardMetrics(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => null),
            fetchStripeDailyBalances(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
            fetchStripeTransactions(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
            fetchStripePayouts(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
          ]);
          return { metrics, daily, txns, pouts };
        })
      );

      for (const { metrics, daily, txns, pouts } of monthResults) {
        if (metrics) {
          computedGross += metrics.gross_volume / 100;
          computedFees += metrics.total_fees / 100;
          computedRefunds += metrics.total_refunds / 100;
          computedNet += metrics.net_revenue / 100;
          computedCount += metrics.charge_count;
          computedDebitGross += (metrics.debit_gross ?? metrics.gross_volume) / 100;
          computedDebitCount += metrics.debit_count ?? metrics.charge_count;
          computedCreditGross += (metrics.credit_gross ?? 0) / 100;
          computedCreditCount += metrics.credit_count ?? 0;
        } else {
          const txnArr = txns as StripeTransaction[];
          const charges = txnArr.filter(t => t.type === 'charge' || t.type === 'payment');
          const refunds = txnArr.filter(t => t.type === 'refund' || t.type === 'payment_refund');
          const chargeGross = charges.reduce((s, t) => s + t.amount, 0) / 100;
          computedGross += chargeGross;
          computedFees += charges.reduce((s, t) => s + t.fee, 0) / 100;
          computedRefunds += refunds.reduce((s, t) => s + Math.abs(t.amount), 0) / 100;
          computedNet += charges.reduce((s, t) => s + t.net, 0) / 100;
          computedCount += charges.length;
          computedDebitGross += chargeGross;
          computedDebitCount += charges.length;
        }
        allDailyBalances = allDailyBalances.concat(daily as StripeDailyBalance[]);
        allTransactions = allTransactions.concat(txns as StripeTransaction[]);
        allPayouts = allPayouts.concat(pouts as StripePayout[]);
      }
    }

    if (computedCount === 0 && allPayments.length > 0) {
      computedNet = allPayments.reduce((sum, p) => sum + p.amount_usd / 100, 0);
      computedCount = allPayments.length;
      computedDebitGross = computedNet;
      computedDebitCount = computedCount;
    }

    allDailyBalances.sort((a, b) => a.date.localeCompare(b.date));
    allTransactions.sort((a, b) => b.created - a.created);
    allPayouts.sort((a, b) => b.created - a.created);

    setTotalGross(computedGross);
    setTotalStripeFees(computedFees);
    setTotalRefunds(computedRefunds);
    setTotalPaymentsUSD(computedNet);
    setPaymentsCount(computedCount);
    setDebitGross(computedDebitGross);
    setDebitCount(computedDebitCount);
    setCreditGross(computedCreditGross);
    setCreditCount(computedCreditCount);

    const computedExpenses = allExpenses.reduce((sum, e) => sum + e.amount_mvr / 100, 0);
    setTotalExpensesMVR(computedExpenses);

    const computedBankRate = selectedBusiness.bank_rate;
    setBankRate(computedBankRate);
    const computedCreditRate = selectedBusiness.credit_card_rate;
    setCreditCardRate(computedCreditRate);
    const computedBmRate = todayRate ? todayRate.black_market_rate : selectedBusiness.default_black_market_rate;
    setBlackMarketRate(computedBmRate);

    setDailyBalances(allDailyBalances);
    setTransactions(allTransactions);
    setPayouts(allPayouts);

    const computedAvg = computedCount > 0 ? computedGross / computedCount : 0;
    setAvgTransactionSize(computedAvg);

    if (stripeKey) {
      setStripeDataLoaded(true);
      fetchStripeBalance(stripeKey).then(setStripeBalance).catch(() => {});
    }

    cacheSet<CachedDashboardData>(buildCacheKey(selectedBusiness.id, 0, 0), {
      totalGross: computedGross,
      totalStripeFees: computedFees,
      totalRefunds: computedRefunds,
      totalPaymentsUSD: computedNet,
      paymentsCount: computedCount,
      totalExpensesMVR: computedExpenses,
      bankRate: computedBankRate,
      creditCardRate: computedCreditRate,
      blackMarketRate: computedBmRate,
      avgTransactionSize: computedAvg,
      debitGross: computedDebitGross,
      debitCount: computedDebitCount,
      creditGross: computedCreditGross,
      creditCount: computedCreditCount,
      dailyBalances: allDailyBalances,
      transactions: allTransactions,
      payouts: allPayouts,
    });
  }

  async function loadMonthData() {
    if (!selectedBusiness) return;

    const range = getMonthRange(selectedMonth.year, selectedMonth.month);
    const paymentDateRange = range;
    const expenseDateRange = { start: range.start.split('T')[0], end: range.end.split('T')[0] };
    const ts = getMonthTimestamps(selectedMonth.year, selectedMonth.month);

    const today = formatDate(new Date());
    const stripeKey = await decryptStripeKey(selectedBusiness.stripe_key_encrypted);

    const metricsPromise = stripeKey
      ? fetchStripeDashboardMetrics(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => null)
      : Promise.resolve(null);

    const stripeDailyPromise = stripeKey
      ? fetchStripeDailyBalances(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
      : Promise.resolve([]);

    const stripeTransactionsPromise = stripeKey
      ? fetchStripeTransactions(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
      : Promise.resolve([]);

    const stripePayoutsPromise = stripeKey
      ? fetchStripePayouts(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
      : Promise.resolve([]);

    const [monthPayments, monthExpenses, todayRate, metrics, daily, txns, pouts] = await Promise.all([
      getPayments(selectedBusiness.id, paymentDateRange),
      getExpenses(selectedBusiness.id, expenseDateRange),
      getDailyRate(selectedBusiness.id, today),
      metricsPromise,
      stripeDailyPromise,
      stripeTransactionsPromise,
      stripePayoutsPromise,
      checkAndResetCards(selectedBusiness.id),
    ]);

    let computedGross = 0;
    let computedFees = 0;
    let computedRefunds = 0;
    let computedNet = 0;
    let computedCount = 0;
    let computedDebitGross = 0;
    let computedDebitCount = 0;
    let computedCreditGross = 0;
    let computedCreditCount = 0;

    if (metrics) {
      computedGross = metrics.gross_volume / 100;
      computedFees = metrics.total_fees / 100;
      computedRefunds = metrics.total_refunds / 100;
      computedNet = metrics.net_revenue / 100;
      computedCount = metrics.charge_count;
      computedDebitGross = (metrics.debit_gross ?? metrics.gross_volume) / 100;
      computedDebitCount = metrics.debit_count ?? metrics.charge_count;
      computedCreditGross = (metrics.credit_gross ?? 0) / 100;
      computedCreditCount = metrics.credit_count ?? 0;
    } else {
      const txnArr = txns as StripeTransaction[];
      const charges = txnArr.filter(t => t.type === 'charge' || t.type === 'payment');
      const refunds = txnArr.filter(t => t.type === 'refund' || t.type === 'payment_refund');
      if (charges.length > 0) {
        computedGross = charges.reduce((s, t) => s + t.amount, 0) / 100;
        computedFees = charges.reduce((s, t) => s + t.fee, 0) / 100;
        computedRefunds = refunds.reduce((s, t) => s + Math.abs(t.amount), 0) / 100;
        computedNet = charges.reduce((s, t) => s + t.net, 0) / 100;
        computedCount = charges.length;
        computedDebitGross = computedGross;
        computedDebitCount = computedCount;
      } else {
        computedNet = monthPayments.reduce((sum, p) => sum + p.amount_usd / 100, 0);
        computedCount = monthPayments.length;
        computedDebitGross = computedNet;
        computedDebitCount = computedCount;
      }
    }

    setTotalGross(computedGross);
    setTotalStripeFees(computedFees);
    setTotalRefunds(computedRefunds);
    setTotalPaymentsUSD(computedNet);
    setPaymentsCount(computedCount);
    setDebitGross(computedDebitGross);
    setDebitCount(computedDebitCount);
    setCreditGross(computedCreditGross);
    setCreditCount(computedCreditCount);

    const computedExpenses = monthExpenses.reduce((sum, e) => sum + e.amount_mvr / 100, 0);
    setTotalExpensesMVR(computedExpenses);

    const computedBankRate = selectedBusiness.bank_rate;
    setBankRate(computedBankRate);
    const computedCreditRate = selectedBusiness.credit_card_rate;
    setCreditCardRate(computedCreditRate);
    const computedBmRate = todayRate ? todayRate.black_market_rate : selectedBusiness.default_black_market_rate;
    setBlackMarketRate(computedBmRate);

    const dailyArr = daily as StripeDailyBalance[];
    setDailyBalances(dailyArr);

    const txnArr = txns as StripeTransaction[];
    setTransactions(txnArr);

    const poutArr = pouts as StripePayout[];
    setPayouts(poutArr);

    const txnCharges = txnArr.filter(t => t.type === 'charge' || t.type === 'payment');
    const computedAvg = txnCharges.length > 0 ? txnCharges.reduce((s, t) => s + t.amount, 0) / txnCharges.length / 100 : 0;
    setAvgTransactionSize(computedAvg);

    if (stripeKey) {
      setStripeDataLoaded(true);
      fetchStripeBalance(stripeKey).then(setStripeBalance).catch(() => {});
    }

    cacheSet<CachedDashboardData>(buildCacheKey(selectedBusiness.id, selectedMonth.year, selectedMonth.month), {
      totalGross: computedGross,
      totalStripeFees: computedFees,
      totalRefunds: computedRefunds,
      totalPaymentsUSD: computedNet,
      paymentsCount: computedCount,
      totalExpensesMVR: computedExpenses,
      bankRate: computedBankRate,
      creditCardRate: computedCreditRate,
      blackMarketRate: computedBmRate,
      avgTransactionSize: computedAvg,
      debitGross: computedDebitGross,
      debitCount: computedDebitCount,
      creditGross: computedCreditGross,
      creditCount: computedCreditCount,
      dailyBalances: dailyArr,
      transactions: txnArr,
      payouts: poutArr,
    });
  }

  async function loadDashboardData() {
    if (!selectedBusiness) return;
    try {
      setRefreshing(true);
      if (isAllTime) {
        await loadAllTimeData();
      } else {
        await loadMonthData();
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const realtimeRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!selectedBusiness) return;

    const channel = supabase
      .channel(`dashboard-realtime-${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        () => {
          if (realtimeRefreshRef.current) clearTimeout(realtimeRefreshRef.current);
          realtimeRefreshRef.current = setTimeout(() => {
            loadDashboardData();
          }, 1500);
        }
      )
      .subscribe();

    return () => {
      if (realtimeRefreshRef.current) clearTimeout(realtimeRefreshRef.current);
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness, selectedMonth, isAllTime]);

  async function updateBlackMarketRate() {
    if (!selectedBusiness) return;
    try {
      const newRate = parseFloat(tempBlackMarketRate);
      if (isNaN(newRate) || newRate <= 0) {
        alert('Please enter a valid rate');
        return;
      }
      const today = formatDate(new Date());
      await setDailyRate(selectedBusiness.id, today, newRate);
      setBlackMarketRate(newRate);
      setShowRateModal(false);
    } catch (error) {
      console.error('Error updating rate:', error);
      alert('Failed to update rate');
    }
  }

  function getMonthOptions() {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: `${getMonthName(date.getMonth() + 1)} ${date.getFullYear()}`,
      });
    }
    return options;
  }

  function isCurrentMonth() {
    if (isAllTime) return false;
    const now = new Date();
    return selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth() + 1;
  }

  const exchangeRevenueMVR = calcExchangeRevenue(totalPaymentsUSD, blackMarketRate);
  const bankCostMVR = calcBankCostSplit(debitGross, creditGross, bankRate, creditCardRate);
  const exchangeProfitMVR = calcExchangeProfitSplit(totalPaymentsUSD, blackMarketRate, debitGross, creditGross, bankRate, creditCardRate);
  const netProfitMVR = calcNetProfit(exchangeProfitMVR, totalExpensesMVR);
  const profitMargin = totalGross > 0 ? ((totalGross - totalStripeFees) / totalGross * 100) : 0;
  const feePercentage = totalGross > 0 ? (totalStripeFees / totalGross * 100) : 0;

  const topCustomers = useMemo(() => {
    const customerMap: Record<string, { name: string; total: number }> = {};
    for (const txn of transactions) {
      if ((txn.type === 'charge' || txn.type === 'payment') && txn.amount > 0) {
        const key = txn.customer_email || txn.customer_name || 'Unknown';
        if (!customerMap[key]) customerMap[key] = { name: txn.customer_name || txn.customer_email || 'Unknown', total: 0 };
        customerMap[key].total += txn.amount / 100;
      }
    }
    return Object.values(customerMap).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [transactions]);

  const txnTypeBreakdown = useMemo(() => {
    const typeMap: Record<string, number> = {};
    for (const txn of transactions) {
      const type = txn.type === 'payment' ? 'charge' : txn.type;
      typeMap[type] = (typeMap[type] || 0) + 1;
    }
    return Object.entries(typeMap).map(([label, value]) => ({
      label: label.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
    })).sort((a, b) => b.value - a.value);
  }, [transactions]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LogoSpinner size={48} />
      </div>
    );
  }

  const views: { key: DashboardView; label: string; icon: typeof Activity }[] = [
    { key: 'overview', label: 'Overview', icon: Activity },
    { key: 'revenue', label: 'Revenue', icon: DollarSign },
    { key: 'transactions', label: 'Activity', icon: BarChart3 },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white tracking-tight">Dashboard</h1>
            <p className="text-sm text-white hidden sm:block">Business performance overview</p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {stripeDataLoaded && views.map(v => (
              <button
                key={v.key}
                onClick={() => setActiveView(v.key)}
                className={`sm:hidden flex items-center p-2 rounded-lg ${
                  activeView === v.key
                    ? 'glass-button-active text-white'
                    : 'glass-button text-white'
                }`}
                title={v.label}
              >
                <v.icon size={14} />
              </button>
            ))}
            {stripeDataLoaded && <div className="sm:hidden w-px h-5 bg-white/15" />}
            <button
              onClick={() => setShowMonthPicker(true)}
              className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:px-3.5 sm:py-2 rounded-xl text-sm ${
                isAllTime ? 'glass-button opacity-60 hover:opacity-100' : 'glass-button'
              }`}
            >
              <Calendar size={15} className="text-teal-400" />
              <span className="hidden sm:inline font-medium text-white text-sm">
                {getMonthName(selectedMonth.month)} {selectedMonth.year}
              </span>
              <ChevronDown size={14} className="hidden sm:inline text-white" />
            </button>
            <button
              onClick={() => setIsAllTime(!isAllTime)}
              className={`p-2 sm:px-3 sm:py-2 text-xs font-semibold rounded-xl ${
                isAllTime
                  ? 'glass-button-active text-white'
                  : 'glass-button text-white'
              }`}
              title="All Time"
            >
              <Clock size={15} className="sm:hidden" />
              <span className="hidden sm:inline">All Time</span>
            </button>
            {!isCurrentMonth() && !isAllTime && (
              <button
                onClick={() => {
                  const now = new Date();
                  selectMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
                }}
                className="hidden sm:block px-3 py-2 text-white text-xs font-semibold rounded-xl glass-button-active"
              >
                Current Month
              </button>
            )}
            <button
              onClick={() => loadDashboardData()}
              disabled={refreshing}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl disabled:opacity-50 text-xs font-medium text-white"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {stripeDataLoaded && (
          <div className="hidden sm:flex items-center gap-1.5 mt-3 overflow-x-auto pb-1">
            {views.map(v => (
              <button
                key={v.key}
                onClick={() => setActiveView(v.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                  activeView === v.key
                    ? 'glass-button-active text-white'
                    : 'glass-button text-white'
                }`}
              >
                <v.icon size={13} />
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {activeView === 'overview' && <OverviewSection
          paymentsCount={paymentsCount}
          totalPaymentsUSD={totalPaymentsUSD}
          exchangeProfitMVR={exchangeProfitMVR}
          netProfitMVR={netProfitMVR}
          totalGross={totalGross}
          totalStripeFees={totalStripeFees}
          avgTransactionSize={avgTransactionSize}
          profitMargin={profitMargin}
          feePercentage={feePercentage}
          bankRate={bankRate}
          creditCardRate={creditCardRate}
          blackMarketRate={blackMarketRate}
          exchangeRevenueMVR={exchangeRevenueMVR}
          bankCostMVR={bankCostMVR}
          totalExpensesMVR={totalExpensesMVR}
          debitGross={debitGross}
          creditGross={creditGross}
          dailyBalances={dailyBalances}
          transactions={transactions}
          payouts={payouts}
          stripeBalance={stripeBalance}
          stripeDataLoaded={stripeDataLoaded}
          isAllTime={isAllTime}
          onRevenueCardClick={() => setShowRevenueModal(true)}
        />}

        {activeView === 'revenue' && <RevenueSection
          dailyBalances={dailyBalances}
          totalGross={totalGross}
          totalStripeFees={totalStripeFees}
          profitMargin={profitMargin}
          totalPaymentsUSD={totalPaymentsUSD}
        />}

        {activeView === 'transactions' && <TransactionSection
          transactions={transactions}
          topCustomers={topCustomers}
          txnTypeBreakdown={txnTypeBreakdown}
        />}
      </div>

      {showRateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-white">Update Black Market Rate</h2>
              <button onClick={() => setShowRateModal(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <p className="text-xs text-white mb-4">Today: {formatDate(new Date())}</p>
            <input
              type="number"
              step="0.0001"
              value={tempBlackMarketRate}
              onChange={(e) => setTempBlackMarketRate(e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-white/10 border border-white/10 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 mb-4"
              placeholder="Enter rate"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowRateModal(false)} className="flex-1 px-4 py-2.5 text-sm text-white rounded-xl font-medium glass-button">
                Cancel
              </button>
              <button onClick={updateBlackMarketRate} className="flex-1 px-4 py-2.5 text-sm text-white rounded-xl font-semibold glass-button-active">
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {showMonthPicker && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-sm max-h-[50vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Select Month</h2>
              <button onClick={() => setShowMonthPicker(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {getMonthOptions().map((option) => (
                <button
                  key={`${option.year}-${option.month}`}
                  onClick={() => {
                    selectMonth({ year: option.year, month: option.month });
                    setShowMonthPicker(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm rounded-xl mb-0.5 transition-all duration-150 ${
                    !isAllTime && selectedMonth.year === option.year && selectedMonth.month === option.month
                      ? 'bg-teal-500/10 text-teal-400 font-semibold'
                      : 'text-white glass-hover'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showRevenueModal && (
        <RevenueDetailModal
          totalPaymentsUSD={totalPaymentsUSD}
          totalGross={totalGross}
          totalStripeFees={totalStripeFees}
          totalRefunds={totalRefunds}
          debitGross={debitGross}
          creditGross={creditGross}
          debitCount={debitCount}
          creditCount={creditCount}
          paymentsCount={paymentsCount}
          bankRate={bankRate}
          creditCardRate={creditCardRate}
          blackMarketRate={blackMarketRate}
          bankCostMVR={bankCostMVR}
          exchangeRevenueMVR={exchangeRevenueMVR}
          exchangeProfitMVR={exchangeProfitMVR}
          onClose={() => setShowRevenueModal(false)}
        />
      )}
    </div>
  );
}

function OverviewSection({
  paymentsCount, totalPaymentsUSD, exchangeProfitMVR, netProfitMVR,
  totalGross, totalStripeFees, avgTransactionSize,
  profitMargin, feePercentage, bankRate, creditCardRate, blackMarketRate,
  exchangeRevenueMVR, bankCostMVR, totalExpensesMVR,
  debitGross, creditGross,
  dailyBalances, transactions, payouts, stripeBalance, stripeDataLoaded, isAllTime,
  onRevenueCardClick,
}: {
  paymentsCount: number; totalPaymentsUSD: number; exchangeProfitMVR: number; netProfitMVR: number;
  totalGross: number; totalStripeFees: number; avgTransactionSize: number;
  profitMargin: number; feePercentage: number; bankRate: number; creditCardRate: number; blackMarketRate: number;
  exchangeRevenueMVR: number; bankCostMVR: number; totalExpensesMVR: number;
  debitGross: number; creditGross: number;
  dailyBalances: StripeDailyBalance[]; transactions: StripeTransaction[]; payouts: StripePayout[]; stripeBalance: StripeBalanceData | null; stripeDataLoaded: boolean;
  isAllTime: boolean;
  onRevenueCardClick: () => void;
}) {
  const periodLabel = isAllTime ? 'all time' : 'this month';
  const statCards = [
    {
      label: 'Total Payments',
      renderValue: () => <AnimatedNumber value={paymentsCount} decimals={0} />,
      sub: `Transactions ${periodLabel}`,
      icon: CreditCard,
      iconBg: 'bg-sky-500/10',
      iconColor: 'text-sky-600',
      tooltip: { title: 'Total Payments', description: 'Total number of successful card transactions processed through Stripe during the selected month.' },
    },
    {
      label: 'USD Revenue',
      renderValue: () => <AnimatedNumber value={totalPaymentsUSD} prefix="$" />,
      sub: 'Tap for breakdown',
      icon: DollarSign,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      tooltip: { title: 'Net USD Revenue', description: 'Total revenue after Stripe fees and refunds have been deducted. Tap to see debit vs credit card breakdown.' },
      onClick: onRevenueCardClick,
    },
    {
      label: 'Exchange Profit',
      renderValue: () => <AnimatedNumber value={exchangeProfitMVR} prefix="MVR " />,
      sub: 'After bank conversion',
      icon: TrendingUp,
      iconBg: 'bg-teal-500/10',
      iconColor: 'text-teal-400',
      valueColor: exchangeProfitMVR >= 0 ? 'text-emerald-400' : 'text-red-600',
      tooltip: { title: 'Exchange Profit', description: `Profit from exchanging USD at the black market rate (${blackMarketRate.toFixed(2)}) vs the bank rate (${bankRate.toFixed(2)}) + 4.2% Stripe fee. Spread per USD: ${Math.abs(bankRate * 1.042 - blackMarketRate).toFixed(4)} MVR.` },
    },
    {
      label: 'Estimated Net Profit',
      renderValue: () => <AnimatedNumber value={netProfitMVR} prefix="MVR " />,
      sub: `After all expenses (${periodLabel})`,
      icon: netProfitMVR >= 0 ? TrendingUp : TrendingDown,
      highlight: true,
      positive: netProfitMVR >= 0,
      tooltip: { title: 'Estimated Net Profit', description: 'Final profit after subtracting all business expenses (loans, salary, personal, other) from the exchange profit. This is your true bottom line.' },
    },
  ];

  const [selectedDailyDate, setSelectedDailyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showDayPicker, setShowDayPicker] = useState(false);

  const chargesByDay = useMemo(() => {
    const map: Record<string, { gross: number; fees: number; net: number; count: number; refunds: number }> = {};
    for (const txn of transactions) {
      if (txn.type !== 'charge' && txn.type !== 'payment') continue;
      const date = new Date(txn.created * 1000).toISOString().split('T')[0];
      if (!map[date]) map[date] = { gross: 0, fees: 0, net: 0, count: 0, refunds: 0 };
      map[date].gross += txn.amount;
      map[date].fees += txn.fee;
      map[date].net += txn.net;
      map[date].count++;
    }
    return map;
  }, [transactions]);

  const dailyChargeBalances: StripeDailyBalance[] = useMemo(() =>
    Object.entries(chargesByDay)
      .map(([date, vals]) => ({ date, ...vals }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    [chargesByDay]
  );

  const selectedDayCharges = chargesByDay[selectedDailyDate];
  const isToday = selectedDailyDate === new Date().toISOString().slice(0, 10);
  const selectedDateLabel = isToday ? 'Today' : new Date(selectedDailyDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const secondaryCards = stripeDataLoaded ? [
    {
      label: 'Gross Volume',
      renderValue: () => <AnimatedNumber value={totalGross} prefix="$" />,
      sub: 'Before fees & refunds',
      icon: ArrowUpRight,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      tooltip: { title: 'Gross Volume', description: 'Total amount charged to customers before any Stripe fees or refunds. This represents your top-line revenue.' },
    },
    {
      label: 'Stripe Fees',
      renderValue: () => <AnimatedNumber value={totalStripeFees} prefix="$" />,
      sub: `${feePercentage.toFixed(1)}% of gross`,
      icon: ArrowDownRight,
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-600',
      tooltip: { title: 'Stripe Processing Fees', description: `Stripe charges approximately 2.9% + 30c per transaction. Your effective fee rate this month is ${feePercentage.toFixed(1)}% of gross volume.` },
    },
    {
      label: 'Avg Transaction',
      renderValue: () => <AnimatedNumber value={avgTransactionSize} prefix="$" />,
      sub: 'Per charge average',
      icon: Activity,
      iconBg: 'bg-sky-500/10',
      iconColor: 'text-sky-600',
      tooltip: { title: 'Average Transaction Size', description: 'The average dollar amount per successful charge. Tracking this helps identify changes in customer spending patterns.' },
    },
  ] : [];

  return (
    <>
      <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {statCards.map((card) => (
          <InfoTooltip key={card.label} title={card.tooltip.title} description={card.tooltip.description} position="bottom">
            <div
              onClick={card.onClick}
              className={`h-full rounded-2xl p-4 transition-all duration-300 ${
                card.onClick ? 'cursor-pointer active:scale-[0.97]' : 'cursor-default'
              } ${
                card.highlight
                  ? card.positive
                    ? 'bg-emerald-600 shadow-lg shadow-emerald-600/20'
                    : 'bg-red-600 shadow-lg shadow-red-600/20'
                  : 'glass-panel hover:shadow-lg hover:-translate-y-0.5'
              }`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  card.highlight ? 'bg-white/20' : card.iconBg
                }`}>
                  <card.icon size={15} className={card.highlight ? 'text-white' : card.iconColor} />
                </div>
                <span className={`text-xs font-medium leading-tight ${card.highlight ? 'text-white/70' : 'text-white'}`}>{card.label}</span>
              </div>
              <p className={`text-lg font-bold tracking-tight ${
                card.highlight ? 'text-white' : card.valueColor || 'text-white'
              }`}>{card.renderValue()}</p>
              <p className={`text-[11px] mt-0.5 ${card.highlight ? 'text-white/70' : 'text-white'}`}>{card.sub}</p>
            </div>
          </InfoTooltip>
        ))}
      </div>

      {stripeDataLoaded && (
        <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 sm:gap-4 mb-4">
          <div
            onClick={() => setShowDayPicker(true)}
            className="h-full glass-panel rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer active:scale-[0.97]"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-500/10">
                <Banknote size={15} className="text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-white block leading-tight">Daily Sales</span>
                <span className="text-[10px] text-teal-400 flex items-center gap-1">
                  <Calendar size={9} />
                  {selectedDateLabel}
                </span>
              </div>
            </div>
            <p className="text-lg font-bold text-white tracking-tight">
              <AnimatedNumber value={selectedDayCharges ? selectedDayCharges.gross / 100 : 0} prefix="$" />
            </p>
            <p className="text-[11px] text-white mt-0.5">
              {selectedDayCharges ? `${selectedDayCharges.count} transaction${selectedDayCharges.count !== 1 ? 's' : ''}` : 'No sales'}
            </p>
          </div>

          {secondaryCards.map((card) => (
            <InfoTooltip key={card.label} title={card.tooltip.title} description={card.tooltip.description} position="bottom">
              <div className="h-full glass-panel rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.iconBg}`}>
                    <card.icon size={15} className={card.iconColor} />
                  </div>
                  <span className="text-xs font-medium text-white">{card.label}</span>
                </div>
                <p className="text-lg font-bold text-white tracking-tight">{card.renderValue()}</p>
                <p className="text-[11px] text-white mt-0.5">{card.sub}</p>
              </div>
            </InfoTooltip>
          ))}
        </div>
      )}

      {showDayPicker && (
        <DayPickerModal
          selectedDate={selectedDailyDate}
          dailyBalances={dailyChargeBalances}
          onSelect={(date) => { setSelectedDailyDate(date); setShowDayPicker(false); }}
          onClose={() => setShowDayPicker(false)}
        />
      )}

      <div className="grid grid-cols-1 fold:grid-cols-2 gap-4 mb-4">
        <div className="glass-panel rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <DollarSign size={16} className="text-teal-400" />
            </div>
            <h2 className="text-sm font-bold text-white">Exchange Rates</h2>
          </div>
          <div className="flex justify-between items-center py-2.5 border-b border-white/10">
            <div>
              <span className="text-white text-sm">Black Market Rate</span>
              <p className="text-[10px] text-white/50">Selling rate</p>
            </div>
            <span className="text-sm font-semibold tabular-nums text-teal-400">{blackMarketRate.toFixed(4)}</span>
          </div>
          <div className="flex justify-between items-center py-2.5 border-b border-white/10">
            <div>
              <span className="text-white text-sm">Stripe Fees</span>
              <p className="text-[10px] text-white/50">Processing fee</p>
            </div>
            <span className="text-sm font-semibold tabular-nums text-amber-500">4.2%</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="glass-inner rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-3">
                <CreditCard size={12} className="text-sky-400" />
                <span className="text-[11px] font-semibold text-white">Debit</span>
              </div>
              <div className="space-y-2.5">
                <div>
                  <p className="text-[10px] text-white/50">Bank Rate</p>
                  <p className="text-sm font-bold text-white tabular-nums">{bankRate.toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50">Effective Cost</p>
                  <p className="text-xs font-semibold text-white/70 tabular-nums">{(bankRate * 1.042).toFixed(4)}</p>
                </div>
                <div className="pt-2.5 border-t border-white/10">
                  <p className="text-[10px] text-white/50">Spread / USD</p>
                  <p className={`text-base font-bold tabular-nums ${(blackMarketRate - bankRate * 1.042) >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
                    {(blackMarketRate - bankRate * 1.042).toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
            <div className="glass-inner rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-3">
                <CreditCard size={12} className="text-amber-500" />
                <span className="text-[11px] font-semibold text-white">Credit</span>
              </div>
              <div className="space-y-2.5">
                <div>
                  <p className="text-[10px] text-white/50">Bank Rate</p>
                  <p className="text-sm font-bold text-white tabular-nums">{creditCardRate.toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50">Effective Cost</p>
                  <p className="text-xs font-semibold text-white/70 tabular-nums">{(creditCardRate * 1.042).toFixed(4)}</p>
                </div>
                <div className="pt-2.5 border-t border-white/10">
                  <p className="text-[10px] text-white/50">Spread / USD</p>
                  <p className={`text-base font-bold tabular-nums ${(blackMarketRate - creditCardRate * 1.042) >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
                    {(blackMarketRate - creditCardRate * 1.042).toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <InfoTooltip title="Monthly P&L Breakdown" description="A step-by-step breakdown showing how your net profit is calculated: starting from exchange revenue, subtracting bank costs and expenses to arrive at the final net profit figure." position="bottom">
          <div className="glass-panel rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <BarChart3 size={16} className="text-sky-600" />
              </div>
              <h2 className="text-sm font-bold text-white">{isAllTime ? 'All-Time' : 'Monthly'} Breakdown</h2>
            </div>
            <div className="space-y-0">
              {[
                { label: 'Exchange Revenue', value: formatCurrency(exchangeRevenueMVR, 'MVR'), color: 'text-white', desc: `$${totalPaymentsUSD.toFixed(2)} x ${blackMarketRate.toFixed(2)}` },
                { label: 'Bank Cost', value: `-${formatCurrency(bankCostMVR, 'MVR')}`, color: 'text-amber-600', desc: creditGross > 0 ? `Debit $${debitGross.toFixed(0)}x${bankRate.toFixed(2)} + Credit $${creditGross.toFixed(0)}x${creditCardRate.toFixed(2)}` : `$${totalGross.toFixed(2)} x ${bankRate.toFixed(2)}` },
                { label: 'Exchange Profit', value: formatCurrency(exchangeProfitMVR, 'MVR'), color: exchangeProfitMVR >= 0 ? 'text-emerald-400' : 'text-red-600', desc: 'Revenue - Bank cost' },
                { label: 'Total Expenses', value: `-${formatCurrency(totalExpensesMVR, 'MVR')}`, color: 'text-amber-600', desc: 'Loans, salary, personal, other' },
              ].map((row, i) => (
                <div key={row.label} className={`flex justify-between items-center py-3 ${i < 3 ? 'border-b border-white/10' : ''}`}>
                  <div>
                    <span className="text-white text-sm">{row.label}</span>
                    <p className="text-[10px] text-white">{row.desc}</p>
                  </div>
                  <span className={`text-sm font-semibold tabular-nums ${row.color}`}>{row.value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-white/10">
                <span className="text-white text-sm font-bold">Net Profit</span>
                <span className={`text-lg font-bold tabular-nums ${netProfitMVR >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                  {formatCurrency(netProfitMVR, 'MVR')}
                </span>
              </div>
            </div>
          </div>
        </InfoTooltip>
      </div>

      {stripeDataLoaded && dailyBalances.length > 0 && (
        <div className="grid grid-cols-1 fold:grid-cols-2 gap-4 mb-4">
          <InfoTooltip title="Daily Revenue Trend" description="Shows how your daily net revenue fluctuates throughout the month. Look for patterns like higher revenue on certain days of the week." position="bottom">
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <Activity size={16} className="text-teal-400" />
                </div>
                <h2 className="text-sm font-bold text-white">Daily Revenue</h2>
              </div>
              <MiniLineChart
                data={dailyBalances.map(d => ({
                  label: d.date.slice(5),
                  value: d.net / 100,
                }))}
                height={180}
                color="#0d9488"
                currency="$"
              />
            </div>
          </InfoTooltip>

          <InfoTooltip title="Revenue vs Fees" description="Compares your gross revenue (solid line) against Stripe processing fees (dashed line). The gap between the lines represents your retained earnings." position="bottom">
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                  <PieChart size={16} className="text-sky-600" />
                </div>
                <h2 className="text-sm font-bold text-white">Revenue vs Fees</h2>
              </div>
              <AreaChart
                data={dailyBalances.map(d => ({
                  label: d.date.slice(5),
                  value: d.gross,
                  value2: d.fees,
                }))}
                height={180}
                color="#0d9488"
                color2="#ef4444"
                currency="$"
                label1="Gross"
                label2="Fees"
              />
            </div>
          </InfoTooltip>
        </div>
      )}

      {stripeDataLoaded && (
        <div className="grid grid-cols-1 fold:grid-cols-2 gap-4">
          {(payouts.length > 0 || (stripeBalance?.upcoming_payouts?.length ?? 0) > 0) && (
            <InfoTooltip title="Payouts" description="Left: recent completed bank payouts from Stripe. Right: upcoming payouts currently in transit to your bank account." position="bottom">
              <div className="glass-panel rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Wallet size={16} className="text-emerald-400" />
                  </div>
                  <h2 className="text-sm font-bold text-white">Payouts</h2>
                </div>
                <div className="flex gap-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-[11px] font-semibold text-white">Recent</span>
                      <span className="text-[10px] text-white/50 ml-auto pr-2">{payouts.filter(p => p.status === 'paid').length}</span>
                    </div>
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-2">
                      {payouts.filter(p => p.status === 'paid').length > 0 ? payouts.filter(p => p.status === 'paid').slice(0, 6).map(p => (
                        <div key={p.id} className="flex items-center justify-between p-2.5 glass-inner rounded-xl">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              p.status === 'paid' ? 'bg-emerald-500' : p.status === 'in_transit' ? 'bg-amber-500' : 'bg-slate-400'
                            }`} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-white">${(p.amount / 100).toFixed(2)}</p>
                              <p className="text-[10px] text-white/60">
                                {new Date(p.arrival_date * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                            p.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : p.status === 'in_transit' ? 'bg-amber-500/10 text-amber-500' : 'bg-white/10 text-white'
                          }`}>
                            {p.status === 'in_transit' ? 'Transit' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                          </span>
                        </div>
                      )) : (
                        <p className="text-[11px] text-white/40 text-center py-4">No recent payouts</p>
                      )}
                    </div>
                  </div>

                  <div className="w-px bg-white/10 mx-3 shrink-0" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-3">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="text-[11px] font-semibold text-white">Upcoming</span>
                      <span className="text-[10px] text-white/50 ml-auto">{stripeBalance?.upcoming_payouts?.length ?? 0}</span>
                    </div>
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                      {stripeBalance?.upcoming_payouts && stripeBalance.upcoming_payouts.length > 0 ? (
                        <>
                          {stripeBalance.upcoming_payouts.map(p => (
                            <div key={p.id} className={`flex items-center justify-between p-2.5 glass-inner rounded-xl ${p.estimated ? 'border border-dashed border-white/10' : ''}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.estimated ? 'bg-sky-400' : 'bg-amber-500'}`} />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-white">
                                    {p.estimated ? '~' : ''}${(p.amount / 100).toFixed(2)}
                                  </p>
                                  <p className="text-[10px] text-white/60">
                                    {p.estimated ? 'Est. arrival' : 'Arrives'} {new Date(p.arrival_date * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                                p.estimated ? 'bg-sky-500/10 text-sky-400' : p.status === 'in_transit' ? 'bg-amber-500/10 text-amber-500' : 'bg-sky-500/10 text-sky-400'
                              }`}>
                                {p.estimated ? 'Estimated' : p.status === 'in_transit' ? 'In Transit' : 'Upcoming'}
                              </span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-[11px] text-white/40">No upcoming payouts</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </InfoTooltip>
          )}

          <InfoTooltip title="Profit Metrics" description="Key performance indicators showing fee efficiency, profit margin on exchange, and the percentage of revenue retained after Stripe processing." position="bottom">
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <Banknote size={16} className="text-teal-400" />
                </div>
                <h2 className="text-sm font-bold text-white">Profit Metrics</h2>
              </div>
              <div className="space-y-3">
                <MetricRow label="Stripe Net Margin" value={`${profitMargin.toFixed(1)}%`} desc="Revenue retained after Stripe fees" color={profitMargin > 95 ? 'text-emerald-400' : 'text-amber-600'} />
                <MetricRow label="Effective Fee Rate" value={`${feePercentage.toFixed(2)}%`} desc="Average fee percentage on gross" color="text-white" />
                <MetricRow label="Exchange Spread" value={`${Math.abs(bankRate * 1.042 - blackMarketRate).toFixed(4)}`} desc="MVR cost per USD after fees" color="text-teal-400" />
                <MetricRow label="Payout Total" value={`$${(payouts.reduce((s, p) => s + p.amount, 0) / 100).toFixed(2)}`} desc={`${payouts.length} payouts ${periodLabel}`} color="text-emerald-400" />
                <MetricRow label="Daily Average" value={`$${dailyBalances.length > 0 ? (dailyBalances.reduce((s, d) => s + d.net, 0) / dailyBalances.length / 100).toFixed(2) : '0.00'}`} desc="Average daily net revenue" color="text-sky-600" />
              </div>
            </div>
          </InfoTooltip>
        </div>
      )}
    </>
  );
}

function MetricRow({ label, value, desc, color }: { label: string; value: string; desc: string; color: string }) {
  return (
    <div className="flex items-center justify-between p-3 glass-inner rounded-xl">
      <div>
        <p className="text-xs font-medium text-white">{label}</p>
        <p className="text-[10px] text-white">{desc}</p>
      </div>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

function RevenueSection({
  dailyBalances, totalGross, totalStripeFees, profitMargin, totalPaymentsUSD,
}: {
  dailyBalances: StripeDailyBalance[]; totalGross: number; totalStripeFees: number;
  profitMargin: number; totalPaymentsUSD: number;
}) {
  const [chartType, setChartType] = useState<'area' | 'bar'>('area');

  if (dailyBalances.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4">
          <DollarSign size={28} className="text-white" />
        </div>
        <h3 className="text-sm font-semibold text-white mb-1">No revenue data</h3>
        <p className="text-xs text-white">Stripe data will appear once transactions are processed</p>
      </div>
    );
  }

  const summaryCards = [
    { label: 'Gross Revenue', value: `$${totalGross.toFixed(2)}`, icon: ArrowUpRight, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400',
      tooltip: { title: 'Gross Revenue', description: 'Total amount charged to all customers before fees and refunds are deducted.' } },
    { label: 'Net Revenue', value: `$${totalPaymentsUSD.toFixed(2)}`, icon: DollarSign, iconBg: 'bg-teal-500/10', iconColor: 'text-teal-400',
      tooltip: { title: 'Net Revenue', description: 'Gross revenue minus Stripe fees and refunds. The actual amount you receive.' } },
    { label: 'Total Fees', value: `$${totalStripeFees.toFixed(2)}`, icon: ArrowDownRight, iconBg: 'bg-amber-500/10', iconColor: 'text-amber-600',
      tooltip: { title: 'Stripe Fees', description: 'Total payment processing fees charged by Stripe across all transactions this month.' } },
    { label: 'Net Margin', value: `${profitMargin.toFixed(1)}%`, icon: Activity, iconBg: 'bg-sky-500/10', iconColor: 'text-sky-600',
      tooltip: { title: 'Net Margin', description: 'Percentage of gross revenue retained after Stripe fees. Higher is better -- most businesses see 96-97%.' } },
  ];

  return (
    <>
      <div className="grid grid-cols-2 fold:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {summaryCards.map(card => (
          <InfoTooltip key={card.label} title={card.tooltip.title} description={card.tooltip.description} position="bottom">
            <div className="glass-panel rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.iconBg}`}>
                  <card.icon size={15} className={card.iconColor} />
                </div>
                <span className="text-xs font-medium text-white">{card.label}</span>
              </div>
              <p className="text-lg font-bold text-white tracking-tight">{card.value}</p>
            </div>
          </InfoTooltip>
        ))}
      </div>

      <div className="glass-panel rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <Activity size={16} className="text-teal-400" />
            </div>
            <h2 className="text-sm font-bold text-white">Revenue Over Time</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setChartType('area')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium ${chartType === 'area' ? 'glass-button-active text-white' : 'glass-button text-white'}`}
            >
              Area
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium ${chartType === 'bar' ? 'glass-button-active text-white' : 'glass-button text-white'}`}
            >
              Bar
            </button>
          </div>
        </div>
        {chartType === 'area' ? (
          <AreaChart
            data={dailyBalances.map(d => ({
              label: d.date.slice(5),
              value: d.gross,
              value2: d.fees,
            }))}
            height={240}
            color="#0d9488"
            color2="#ef4444"
            currency="$"
            label1="Gross"
            label2="Fees"
          />
        ) : (
          <BarChart
            data={dailyBalances.map(d => ({
              label: d.date.slice(5),
              value: d.net / 100,
              color: d.net >= 0 ? '#0d9488' : '#ef4444',
            }))}
            height={240}
            currency="$"
          />
        )}
      </div>

      <div className="grid grid-cols-1 fold:grid-cols-2 gap-4">
        <InfoTooltip title="Daily Transaction Volume" description="Number of transactions processed each day. Higher bars indicate busier days." position="bottom">
          <div className="glass-panel rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <BarChart3 size={16} className="text-sky-600" />
              </div>
              <h2 className="text-sm font-bold text-white">Daily Volume</h2>
            </div>
            <BarChart
              data={dailyBalances.map(d => ({
                label: d.date.slice(8),
                value: d.count,
                color: '#0ea5e9',
              }))}
              height={160}
              showValues={true}
            />
          </div>
        </InfoTooltip>

        <InfoTooltip title="Cumulative Net Revenue" description="Running total of net revenue accumulated throughout the month. The upward slope indicates positive revenue growth." position="bottom">
          <div className="glass-panel rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp size={16} className="text-emerald-400" />
              </div>
              <h2 className="text-sm font-bold text-white">Cumulative Revenue</h2>
            </div>
            <MiniLineChart
              data={(() => {
                let cum = 0;
                return dailyBalances.map(d => {
                  cum += d.net / 100;
                  return { label: d.date.slice(5), value: cum };
                });
              })()}
              height={160}
              color="#059669"
              currency="$"
            />
          </div>
        </InfoTooltip>
      </div>
    </>
  );
}

function TransactionSection({
  transactions, topCustomers, txnTypeBreakdown,
}: {
  transactions: StripeTransaction[];
  topCustomers: { name: string; total: number }[];
  txnTypeBreakdown: { label: string; value: number }[];
}) {
  const [sortBy, setSortBy] = useState<'amount' | 'date'>('date');

  const recentCharges = transactions
    .filter(t => t.type === 'charge' || t.type === 'payment')
    .sort((a, b) => sortBy === 'date' ? b.created - a.created : Math.abs(b.amount) - Math.abs(a.amount))
    .slice(0, 10);

  const peakHours = (() => {
    const hourMap: Record<number, number> = {};
    for (const txn of transactions) {
      if (txn.type === 'charge' || txn.type === 'payment') {
        const hour = new Date(txn.created * 1000).getHours();
        hourMap[hour] = (hourMap[hour] || 0) + 1;
      }
    }
    return Object.entries(hourMap)
      .map(([hour, count]) => ({ label: `${String(hour).padStart(2, '0')}:00`, value: count }))
      .sort((a, b) => parseInt(a.label) - parseInt(b.label));
  })();

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4">
          <Activity size={28} className="text-white" />
        </div>
        <h3 className="text-sm font-semibold text-white mb-1">No transaction data</h3>
        <p className="text-xs text-white">Stripe activity will appear once transactions are processed</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 fold:grid-cols-2 gap-4 mb-4">
        <InfoTooltip title="Transaction Types" description="Breakdown of all Stripe balance transactions by type. Charges represent customer payments, while other types include fees, refunds, and payouts." position="bottom">
          <div className="glass-panel rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                <PieChart size={16} className="text-teal-400" />
              </div>
              <h2 className="text-sm font-bold text-white">Transaction Types</h2>
            </div>
            <DonutChart
              data={txnTypeBreakdown}
              size={170}
              centerLabel="Total"
              centerValue={transactions.length.toString()}
            />
          </div>
        </InfoTooltip>

        <InfoTooltip title="Top Customers" description="Your highest-spending customers this month ranked by total charge volume. Focus retention efforts on these high-value accounts." position="bottom">
          <div className="glass-panel rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <CreditCard size={16} className="text-sky-600" />
              </div>
              <h2 className="text-sm font-bold text-white">Top Customers</h2>
            </div>
            {topCustomers.length > 0 ? (
              <div className="space-y-2">
                {topCustomers.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 glass-inner rounded-xl">
                    <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-400 text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate">{c.name}</p>
                    </div>
                    <span className="text-xs font-bold text-white tabular-nums">${c.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white text-center py-8">No customer data available</p>
            )}
          </div>
        </InfoTooltip>
      </div>

      {peakHours.length > 0 && (
        <InfoTooltip title="Peak Transaction Hours" description="Shows what time of day most charges occur. Use this data to optimize staffing and support availability during peak hours." position="bottom">
          <div className="glass-panel rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock size={16} className="text-amber-600" />
              </div>
              <h2 className="text-sm font-bold text-white">Peak Hours</h2>
            </div>
            <BarChart
              data={peakHours}
              height={160}
              showValues={true}
            />
          </div>
        </InfoTooltip>
      )}

      <div className="glass-panel rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Activity size={16} className="text-emerald-400" />
            </div>
            <h2 className="text-sm font-bold text-white">Recent Charges</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={12} className="text-white" />
            <button
              onClick={() => setSortBy('date')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium ${sortBy === 'date' ? 'glass-button-active text-white' : 'glass-button text-white'}`}
            >
              Latest
            </button>
            <button
              onClick={() => setSortBy('amount')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium ${sortBy === 'amount' ? 'glass-button-active text-white' : 'glass-button text-white'}`}
            >
              Highest
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {recentCharges.map(txn => (
            <div key={txn.id} className="flex items-center justify-between p-3 glass-inner rounded-xl glass-hover">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <ArrowUpRight size={14} className="text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-white truncate">
                    {txn.customer_name || txn.customer_email || txn.description || 'Charge'}
                  </p>
                  <p className="text-[10px] text-white">
                    {new Date(txn.created * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <p className="text-xs font-bold text-emerald-400">${(txn.amount / 100).toFixed(2)}</p>
                <p className="text-[10px] text-white">Fee: ${(txn.fee / 100).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
